import { useMemo, useRef, useState } from "react";
import GridViewer, { type GridViewerHandle } from "../../viewer/GridViewer";
import type { ViewerContextState } from "../../viewer/context-state";
import type { Parcel } from "../../viewer/types";
import type { ParcelGeom, ReviewItem } from "../../lib/review-api";

/**
 * EmbeddedClientGrid — the REAL Grid canvas (same engine as the public map)
 * embedded inside the Admin Clients detail, framed on the selected client's
 * parcels. The admin can zoom/pan and SEE the actual published content, then
 * click any parcel: if it belongs to one of this client's Territories the
 * moderation Inspector opens for that Territory; otherwise the shell shows a
 * "no published content to moderate" note with the raw parcel info.
 *
 * Clicking NEVER navigates away (the old schematic map deep-linked to `#grid`).
 */
export function EmbeddedClientGrid({
  parcels,
  territories,
  onPickTerritory,
  onPickBareParcel,
}: {
  parcels: ParcelGeom[];
  territories: ReviewItem[];
  onPickTerritory: (identityId: string) => void;
  onPickBareParcel: (parcel: Parcel) => void;
}) {
  const ref = useRef<GridViewerHandle>(null);
  const [refocusNonce, setRefocusNonce] = useState(0);

  // Frame target = bounding box of every parcel this client owns.
  const focus = useMemo(() => {
    if (parcels.length === 0) return null;
    const minX = Math.min(...parcels.map((p) => p.x));
    const minY = Math.min(...parcels.map((p) => p.y));
    const maxX = Math.max(...parcels.map((p) => p.x + p.w));
    const maxY = Math.max(...parcels.map((p) => p.y + p.h));
    const span = Math.max(14, Math.max(maxX - minX, maxY - minY) * 1.4);
    // refocusNonce is intentionally read so a manual re-frame produces a new
    // object identity → the viewer re-applies the same framing.
    void refocusNonce;
    return { fx: (minX + maxX) / 2, fy: (minY + maxY) / 2, span, ov: true };
  }, [parcels, refocusNonce]);

  // parcel id → owning Territory identity (from the client's memberships).
  const parcelToTerritory = useMemo(() => {
    const m = new Map<number, string>();
    for (const t of territories) {
      for (const pid of t.member_parcel_ids ?? []) m.set(Number(pid), t.identity_id);
    }
    return m;
  }, [territories]);

  const handleCtx = (s: ViewerContextState) => {
    if (s.kind !== "parcel") return;
    const p = s.detail.parcel;
    const tid = parcelToTerritory.get(Number(p.id));
    if (tid) onPickTerritory(tid);
    else onPickBareParcel(p);
  };

  return (
    <div
      data-testid="client-grid"
      className="relative w-full h-[calc(100vh-230px)] min-h-[460px] overflow-hidden rounded-md border border-border-soft bg-[#05080d]"
    >
      <div className="absolute inset-0">
        <GridViewer
          ref={ref}
          layout="fill"
          chrome="external"
          focus={focus}
          onContextStateChange={handleCtx}
        />
      </div>

      {/* Zoom / frame controls (external chrome → shell owns them). */}
      <div data-testid="client-grid-controls" className="absolute right-3 top-3 z-10 flex flex-col gap-1.5">
        <GridBtn testid="client-grid-zoom-in" label="Zoom in" onClick={() => ref.current?.zoomIn()}>
          <path d="M7 3v8M3 7h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </GridBtn>
        <GridBtn testid="client-grid-zoom-out" label="Zoom out" onClick={() => ref.current?.zoomOut()}>
          <path d="M3 7h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </GridBtn>
        <GridBtn testid="client-grid-reframe" label="Frame client" onClick={() => setRefocusNonce((n) => n + 1)}>
          <path d="M2 5V2h3M12 5V2H9M2 9v3h3M12 9v3H9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
        </GridBtn>
      </div>

      <div className="absolute left-3 bottom-3 z-10 rounded-md border border-border-soft bg-obsidian/70 px-2.5 py-1.5 font-mono text-[9px] uppercase tracking-[0.14em] text-text-muted backdrop-blur">
        Click a parcel to inspect · drag to pan · Ctrl+wheel to zoom
      </div>
    </div>
  );
}

function GridBtn({
  testid,
  label,
  onClick,
  children,
}: {
  testid: string;
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      data-testid={testid}
      aria-label={label}
      onClick={onClick}
      className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border-soft bg-obsidian/70 text-text-secondary backdrop-blur transition-colors hover:text-neon-teal hover:border-border-accent"
    >
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>{children}</svg>
    </button>
  );
}
